import { useMemo, useState, type CSSProperties } from 'react';
import {
  useParity,
  useRunParity,
  useCoverage,
  useCertifications,
  useCertification,
  type ParityRow,
  type ParityStatus,
  type ParityVerdict,
  type Coverage,
  type CoverageCustomer,
  type CoverageCell,
  type CertListItem,
  type CertReport,
  type CertStatus,
} from '../api/cutover.js';

/**
 * CutoverDashboard — visualizes the Jasper → Sage migration parity:
 * renders every migrated report instance through Sage's real render path,
 * shows per-report results, per-customer rollups, and the error breakdown,
 * and lets an admin re-run the whole sweep on demand. Automated, not manual.
 */
const STATUS_COLOR: Record<ParityStatus, string> = {
  data: 'var(--color-success-fg, #15803d)',
  zero: 'var(--color-text-tertiary, #697184)',
  error: 'var(--color-danger-fg, #b91c1c)',
};
const STATUS_BG: Record<ParityStatus, string> = {
  data: 'var(--color-success-softer, #dcfce7)',
  zero: 'var(--color-surface-2, #f3f5f8)',
  error: 'var(--color-danger-softer, #fee2e2)',
};
const CAT_LABEL: Record<string, string> = {
  missing_sp: 'Missing procedure (upstream)',
  broken_sp: 'Broken procedure (upstream)',
  template: 'Sage render bug',
  timeout: 'Timeout (perf)',
  other: 'Other',
};

export function CutoverDashboard(): JSX.Element {
  const q = useParity();
  const run = useRunParity();
  const cov = useCoverage();
  const [statusFilter, setStatusFilter] = useState<ParityStatus | 'all'>('all');
  const [customerFilter, setCustomerFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const snap = q.data;
  const rows = snap?.rows ?? [];
  const s = snap?.summary;
  const running = snap?.status === 'running';

  const customers = useMemo(() => Array.from(new Set(rows.map((r) => r.customer))).sort(), [rows]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (statusFilter === 'all' || r.status === statusFilter) &&
        (customerFilter === 'all' || r.customer === customerFilter) &&
        (!needle ||
          r.instanceName.toLowerCase().includes(needle) ||
          r.templateName.toLowerCase().includes(needle) ||
          (r.sp ?? '').toLowerCase().includes(needle)),
    );
  }, [rows, statusFilter, customerFilter, search]);

  const pct = (n: number): string => (s && s.total ? `${Math.round((n / s.total) * 100)}%` : '—');

  return (
    <div
      className="content-body"
      style={{ maxWidth: 1200, margin: '0 auto', padding: 'var(--space-5)' }}
    >
      <div
        className="row"
        style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-4)' }}
      >
        <div>
          <h1 style={{ margin: 0 }}>Cutover Parity</h1>
          <p className="text-dim" style={{ margin: '4px 0 0' }}>
            Jasper → Sage migration readiness. Every migrated report executed through Sage's data
            path — the same Canopy stored procedure Jasper calls, with the migrated parameters — and
            its row count compared.
          </p>
        </div>
        <button
          type="button"
          className="btn btn--primary"
          disabled={running}
          onClick={() => run.mutate()}
        >
          {running ? `Running… ${snap?.completed ?? 0}/${snap?.total ?? 0}` : 'Run full sweep'}
        </button>
      </div>

      {running && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Progress value={snap?.completed ?? 0} max={snap?.total || 1} />
        </div>
      )}

      <CertificationSection />

      {!s || s.total === 0 ? (
        <div className="empty-state" style={{ marginTop: 'var(--space-6)' }}>
          <h3>No parity run yet</h3>
          <p className="text-dim">
            Click “Run full sweep” to render every migrated report and see the comparison.
          </p>
        </div>
      ) : (
        <>
          {/* Migration-readiness verdict — the meaningful grouping. A
              successful render (data OR zero) means Sage ran the same Canopy
              procedure with the migrated Jasper parameters and returned its
              result == Jasper. Zero ≠ broken: the customer simply has no data
              for that report/window (Jasper shows the same). The only true
              Sage work is the "Sage defect" bucket. */}
          {(() => {
            const v = s.verdicts ?? { match: s.data + s.zero, sage_bug: 0, upstream: 0, slow: 0 };
            const readyPct = s.total ? Math.round((v.match / s.total) * 100) : 0;
            return (
              <>
                <div style={banner}>
                  <div>
                    <div style={{ fontSize: '2.4rem', fontWeight: 800, lineHeight: 1 }}>
                      {readyPct}%
                    </div>
                    <div className="text-dim text-sm">
                      exact match ({v.match}/{s.total})
                    </div>
                  </div>
                  <p style={{ margin: 0, flex: 1, minWidth: 280 }} className="text-sm">
                    <strong>{v.match} reports</strong> where Sage's row count equals the Canopy
                    procedure's own output — and since Jasper calls the same procedure with the same
                    parameters, that <em>is</em> Jasper. A zero counts as a match (the customer has
                    no data; Jasper shows zero too).
                    <strong> {v.sage_bug} Sage defects.</strong> The rest is upstream procedure or
                    perf, below.
                  </p>
                </div>
                <div style={cardGrid}>
                  <Stat
                    label="Exact match (Sage = Jasper)"
                    value={v.match}
                    sub={`${readyPct}%`}
                    color={STATUS_COLOR.data}
                  />
                  <Stat
                    label="Sage defects"
                    value={v.sage_bug}
                    sub={v.sage_bug === 0 ? 'none 🎉' : 'procedure has data, render fails'}
                    color={v.sage_bug ? STATUS_COLOR.error : STATUS_COLOR.data}
                  />
                  <Stat
                    label="Upstream procedure"
                    value={v.upstream}
                    sub="missing/broken in Canopy DB"
                    color="var(--color-warning-fg, #b45309)"
                  />
                  <Stat
                    label="Slow (timeout)"
                    value={v.slow}
                    sub="works, perf-tune"
                    color={STATUS_COLOR.zero}
                  />
                </div>
              </>
            );
          })()}

          {/* Raw breakdown (Sage's literal output) */}
          <div style={{ ...cardGrid, marginTop: 'var(--space-3)' }}>
            <Stat
              label="Reports tested"
              value={s.total}
              sub={snap?.finishedAt ? `last run ${ago(snap.finishedAt)}` : 'in progress'}
            />
            <Stat
              label="Returning data"
              value={s.data}
              sub={pct(s.data)}
              color={STATUS_COLOR.data}
            />
            <Stat
              label="Zero rows (matches Jasper)"
              value={s.zero}
              sub={`${pct(s.zero)} · genuine-empty`}
              color={STATUS_COLOR.zero}
            />
            <Stat label="Errored" value={s.error} sub={pct(s.error)} color={STATUS_COLOR.error} />
          </div>

          {/* Error categories */}
          {s.byCategory.length > 0 && (
            <section style={{ marginTop: 'var(--space-5)' }}>
              <h3 style={sectionH}>Errors by cause</h3>
              <div className="row" style={{ flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {s.byCategory.map((c) => (
                  <span key={c.category} className="badge" style={chip}>
                    <strong>{c.count}</strong> {CAT_LABEL[c.category] ?? c.category}
                  </span>
                ))}
              </div>
              <p className="text-dim text-sm" style={{ marginTop: 6 }}>
                “Upstream” = the Canopy stored procedure itself is missing or broken — fails for
                Jasper too, not a Sage defect.
              </p>
            </section>
          )}

          {/* Per-customer rollup */}
          <section style={{ marginTop: 'var(--space-5)' }}>
            <h3 style={sectionH}>By customer</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {s.byCustomer.map((c) => (
                <button
                  key={c.customer}
                  type="button"
                  onClick={() =>
                    setCustomerFilter(customerFilter === c.customer ? 'all' : c.customer)
                  }
                  style={{
                    ...custRow,
                    outline:
                      customerFilter === c.customer ? '2px solid var(--color-accent-fg)' : 'none',
                  }}
                >
                  <span
                    style={{
                      width: 200,
                      textAlign: 'left',
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.customer}
                  </span>
                  <span
                    style={{
                      flex: 1,
                      display: 'flex',
                      height: 18,
                      borderRadius: 4,
                      overflow: 'hidden',
                      background: 'var(--color-surface-2)',
                    }}
                  >
                    {c.data > 0 && (
                      <span
                        title={`${c.data} with data`}
                        style={{
                          width: `${(c.data / c.total) * 100}%`,
                          background: STATUS_COLOR.data,
                        }}
                      />
                    )}
                    {c.zero > 0 && (
                      <span
                        title={`${c.zero} zero rows`}
                        style={{
                          width: `${(c.zero / c.total) * 100}%`,
                          background: 'var(--color-border)',
                        }}
                      />
                    )}
                    {c.error > 0 && (
                      <span
                        title={`${c.error} errored`}
                        style={{
                          width: `${(c.error / c.total) * 100}%`,
                          background: STATUS_COLOR.error,
                        }}
                      />
                    )}
                  </span>
                  <span className="text-dim text-sm" style={{ width: 130, textAlign: 'right' }}>
                    {c.data}/{c.total} data{c.error ? ` · ${c.error} err` : ''}
                  </span>
                </button>
              ))}
            </div>
          </section>

          {/* Per-customer migration coverage matrix */}
          {cov.data && (
            <section style={{ marginTop: 'var(--space-6)' }}>
              <h3 style={sectionH}>Migration coverage</h3>
              <CoverageMatrix
                cov={cov.data}
                activeCustomer={customerFilter}
                onPick={(c) => setCustomerFilter(customerFilter === c ? 'all' : c)}
              />
            </section>
          )}

          {/* Instance table */}
          <section style={{ marginTop: 'var(--space-6)' }}>
            <div
              className="row"
              style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)' }}
            >
              <h3 style={sectionH}>Every report ({filtered.length})</h3>
              <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <input
                  className="input"
                  placeholder="Search report / SP…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ minWidth: 200 }}
                />
                <select
                  className="input"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as ParityStatus | 'all')}
                >
                  <option value="all">All statuses</option>
                  <option value="data">Data</option>
                  <option value="zero">Zero rows</option>
                  <option value="error">Errored</option>
                </select>
                <select
                  className="input"
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                >
                  <option value="all">All customers</option>
                  {customers.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ overflowX: 'auto', marginTop: 'var(--space-2)' }}>
              <table className="data-table" style={{ width: '100%', fontSize: '0.8125rem' }}>
                <thead>
                  <tr>
                    <th>Verdict</th>
                    <th>Customer</th>
                    <th>Report</th>
                    <th>Template</th>
                    <th>Procedure</th>
                    <th style={{ textAlign: 'right' }}>Sage</th>
                    <th style={{ textAlign: 'right' }}>Jasper</th>
                    <th style={{ textAlign: 'center' }}>=</th>
                    <th style={{ textAlign: 'right' }}>ms</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <Row key={r.instanceId} r={r} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

const VERDICT: Record<ParityVerdict, { label: string; color: string; bg: string; mark: string }> = {
  match: { label: 'match', color: STATUS_COLOR.data, bg: STATUS_BG.data, mark: '✓' },
  sage_bug: { label: 'Sage bug', color: STATUS_COLOR.error, bg: STATUS_BG.error, mark: '✗' },
  upstream: {
    label: 'upstream',
    color: 'var(--color-warning-fg, #b45309)',
    bg: 'var(--color-warning-softer, #fef3c7)',
    mark: '⬆',
  },
  slow: { label: 'slow', color: STATUS_COLOR.zero, bg: STATUS_BG.zero, mark: '⏱' },
};

function Row({ r }: { r: ParityRow }): JSX.Element {
  const v = VERDICT[r.verdict];
  const sage = r.rowCount ?? (r.status === 'error' ? '—' : 0);
  const jasper =
    r.procRowCount ?? (r.verdict === 'upstream' ? '—' : r.verdict === 'slow' ? '?' : sage);
  return (
    <tr>
      <td>
        <span style={{ ...badge, color: v.color, background: v.bg }}>{v.label}</span>
      </td>
      <td>{r.customer}</td>
      <td title={r.clientId}>{r.instanceName}</td>
      <td className="text-dim">{r.templateName.replace(/ \[Jasper #.*\]$/, '')}</td>
      <td
        className="text-dim"
        style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: '0.75rem' }}
      >
        {r.sp ?? '— raw SQL —'}
      </td>
      <td style={{ textAlign: 'right', fontWeight: 600 }}>{sage}</td>
      <td style={{ textAlign: 'right', fontWeight: 600 }}>{jasper}</td>
      <td style={{ textAlign: 'center', color: v.color, fontWeight: 700 }} title={v.label}>
        {v.mark}
      </td>
      <td style={{ textAlign: 'right' }} className="text-dim">
        {r.durationMs ?? ''}
      </td>
      <td
        className="text-dim"
        style={{
          maxWidth: 300,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={r.errorMessage ?? ''}
      >
        {r.category ? `${CAT_LABEL[r.category] ?? r.category}: ${r.errorMessage ?? ''}` : ''}
      </td>
    </tr>
  );
}

function Stat({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: number;
  sub?: string;
  color?: string;
}): JSX.Element {
  return (
    <div className="card" style={{ padding: 'var(--space-4)' }}>
      <div className="text-dim text-sm">{label}</div>
      <div
        style={{
          fontSize: '2rem',
          fontWeight: 700,
          color: color ?? 'var(--color-text-primary)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {sub && (
        <div className="text-dim text-xs" style={{ marginTop: 2 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Progress({ value, max }: { value: number; max: number }): JSX.Element {
  return (
    <div
      style={{
        height: 8,
        borderRadius: 4,
        background: 'var(--color-surface-2)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          width: `${Math.round((value / max) * 100)}%`,
          height: '100%',
          background: 'var(--color-accent-fg)',
          transition: 'width .3s',
        }}
      />
    </div>
  );
}

function ago(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
}

// ── Migration coverage matrix ───────────────────────────────────────────────
type CellStatus = 'ok' | 'warn' | 'gap' | 'none';
const CELL_BG: Record<CellStatus, string> = {
  ok: 'var(--color-success-softer, #dcfce7)',
  warn: 'var(--color-warning-softer, #fef3c7)',
  gap: 'var(--color-danger-softer, #fee2e2)',
  none: 'transparent',
};
const CELL_FG: Record<CellStatus, string> = {
  ok: 'var(--color-success-fg, #15803d)',
  warn: 'var(--color-warning-fg, #b45309)',
  gap: 'var(--color-danger-fg, #b91c1c)',
  none: 'var(--color-text-tertiary, #697184)',
};
function covCell(text: string, status: CellStatus, key: number): JSX.Element {
  return (
    <td
      key={key}
      style={{
        textAlign: 'center',
        padding: '7px 10px',
        background: CELL_BG[status],
        color: CELL_FG[status],
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </td>
  );
}
function compareCell(c: CoverageCell): { text: string; status: CellStatus } {
  if (c.expected === null) return { text: String(c.actual), status: c.actual > 0 ? 'ok' : 'none' };
  return { text: `${c.expected} / ${c.actual}`, status: c.actual >= c.expected ? 'ok' : 'gap' };
}
function paramCell(p: { reports: number; covered: number; gaps: number }): {
  text: string;
  status: CellStatus;
} {
  return { text: `${p.covered} / ${p.reports}`, status: p.gaps > 0 ? 'gap' : 'ok' };
}
function settingsCell(s: { reports: number; configured: number }): {
  text: string;
  status: CellStatus;
} {
  // 0 report-level overrides isn't a gap — those reports inherit page/audience
  // settings from their template. Neutral, not a warning. Partial = warn.
  if (s.configured === 0) return { text: `0 / ${s.reports}`, status: 'none' };
  return {
    text: `${s.configured} / ${s.reports}`,
    status: s.configured >= s.reports ? 'ok' : 'warn',
  };
}
function dataParityCell(d: CoverageCustomer['dataParity']): { text: string; status: CellStatus } {
  if (!d) return { text: '—', status: 'none' };
  if (d.sage_bug > 0) return { text: `${d.match} / ${d.total}`, status: 'gap' };
  if (d.match < d.total) return { text: `${d.match} / ${d.total}`, status: 'warn' };
  return { text: `${d.match} / ${d.total}`, status: 'ok' };
}

function CoverageMatrix({
  cov,
  activeCustomer,
  onPick,
}: {
  cov: Coverage;
  activeCustomer: string;
  onPick: (c: string) => void;
}): JSX.Element {
  const cols = [
    'Templates',
    'Reports',
    'Parameters',
    'Settings',
    'Subscriptions',
    'Datasources',
    'Data parity',
  ];
  const renderRow = (c: CoverageCustomer, isTotal: boolean): JSX.Element => {
    const cells = [
      compareCell(c.templates),
      compareCell(c.reports),
      paramCell(c.parameters),
      settingsCell(c.settings),
      compareCell(c.subscriptions),
      compareCell(c.datasources),
      dataParityCell(c.dataParity),
    ];
    const active = !isTotal && activeCustomer === c.customer;
    const bg = isTotal
      ? 'var(--color-surface-2)'
      : active
        ? 'var(--color-accent-softer, #eef2ff)'
        : 'var(--color-surface-1)';
    return (
      <tr
        key={c.customer}
        onClick={isTotal ? undefined : () => onPick(c.customer)}
        style={{
          cursor: isTotal ? 'default' : 'pointer',
          background: bg,
          borderBottom: '1px solid var(--color-border-subtle, #eee)',
        }}
      >
        <td
          style={{
            textAlign: 'left',
            padding: '7px 12px',
            position: 'sticky',
            left: 0,
            background: bg,
            fontWeight: isTotal ? 600 : 500,
            whiteSpace: 'nowrap',
          }}
        >
          {c.customer}
        </td>
        {cells.map((cell, i) => covCell(cell.text, cell.status, i))}
      </tr>
    );
  };
  return (
    <>
      <div
        style={{
          overflowX: 'auto',
          border: '1px solid var(--color-border-subtle, #e5e7eb)',
          borderRadius: 'var(--radius-md)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--color-border)',
                textAlign: 'center',
                color: 'var(--color-text-secondary)',
              }}
            >
              <th
                style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  position: 'sticky',
                  left: 0,
                  background: 'var(--color-surface-1)',
                  fontWeight: 500,
                }}
              >
                Customer
              </th>
              {cols.map((col) => (
                <th key={col} style={{ padding: '8px 10px', fontWeight: 500 }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderRow(cov.totals, true)}
            {cov.customers.map((c) => renderRow(c, false))}
          </tbody>
        </table>
      </div>
      <p className="text-dim text-sm" style={{ marginTop: 6 }}>
        Two-number cells are <strong>Jasper expected / Sage actual</strong>
        {cov.manifestCapturedAt
          ? ''
          : ' — Jasper snapshot not captured yet, showing Sage actual only'}
        . Parameters show <strong>required-filter coverage</strong>; Settings show{' '}
        <strong>report-level overrides</strong> (0 = inherits the template); Data parity shows{' '}
        <strong>match / tested</strong>
        {cov.parityRanAt ? '' : ' (run a sweep above to populate)'}. Click a customer to filter the
        report table below.
      </p>
    </>
  );
}

const cardGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 'var(--space-3)',
  marginTop: 'var(--space-5)',
};
const banner: CSSProperties = {
  display: 'flex',
  gap: 'var(--space-4)',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginTop: 'var(--space-5)',
  padding: 'var(--space-4)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-success-softer, #dcfce7)',
  border: '1px solid var(--color-success-border, #86efac)',
};
const sectionH: CSSProperties = {
  margin: '0 0 var(--space-2)',
  fontSize: 'var(--font-size-md, 1rem)',
};
const chip: CSSProperties = {
  background: 'var(--color-surface-2)',
  padding: '4px 10px',
  borderRadius: 999,
};
const badge: CSSProperties = {
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
};
const custRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-3)',
  padding: '6px 8px',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  width: '100%',
};

// ─── Certification (per-customer end-to-end validation) ──────────────────────

const CERT_META: Record<CertStatus, { label: string; fg: string; bg: string }> = {
  PASS: { label: 'Pass', fg: '#00776b', bg: '#e5f4f2' },
  EMPTY_DEAD: { label: 'Empty · dead loc', fg: '#5b6472', bg: '#f0f2f7' },
  EMPTY_SUSPECT: { label: 'Empty · suspect', fg: '#8a5a06', bg: '#fff5e6' },
  EMPTY: { label: 'Empty', fg: '#5b6472', bg: '#f0f2f7' },
  FAIL_RENDER: { label: 'Fail · render', fg: '#b00f3a', bg: '#fdeaef' },
  FAIL_PARITY: { label: 'Fail · parity', fg: '#b00f3a', bg: '#fdeaef' },
  FAIL_ENABLED: { label: 'Fail · enabled', fg: '#b00f3a', bg: '#fdeaef' },
};

function certPill(status: CertStatus): JSX.Element {
  const m = CERT_META[status] ?? CERT_META.EMPTY;
  return (
    <span
      style={{
        background: m.bg,
        color: m.fg,
        borderRadius: 999,
        padding: '2px 9px',
        fontSize: '0.72rem',
        fontWeight: 700,
        whiteSpace: 'nowrap',
      }}
    >
      {m.label}
    </span>
  );
}

function CertificationSection(): JSX.Element {
  const list = useCertifications();
  const [open, setOpen] = useState<string | null>(null);
  const items = list.data ?? [];

  return (
    <section style={{ marginTop: 'var(--space-6)' }}>
      <h3 style={sectionH}>Certification — what we have validated</h3>
      <p className="text-dim text-sm" style={{ margin: '2px 0 10px' }}>
        Per-customer end-to-end results from the certification harness: every report rendered
        against live production data, its row count captured, empties classified (dead location vs
        suspect), and config parity confirmed. All subscriptions remain disabled.
      </p>
      {items.length === 0 ? (
        <div className="empty-state">
          <p className="text-dim">
            No customer has been certified yet. Run{' '}
            <code>certify-customer.mjs &lt;Customer&gt;</code>.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {items.map((it) => (
            <CertCustomerRow
              key={it.customer}
              item={it}
              open={open === it.customer}
              onToggle={() => setOpen(open === it.customer ? null : it.customer)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function CertCustomerRow({
  item,
  open,
  onToggle,
}: {
  item: CertListItem;
  open: boolean;
  onToggle: () => void;
}): JSX.Element {
  const s = item.summary;
  const clean =
    s.failRender === 0 && (s.parityMismatch ?? s.failParity) === 0 && s.emptySuspect === 0;
  const chip = (label: string, n: number, tone: string) =>
    n > 0 ? (
      <span style={{ fontSize: '0.75rem', color: tone, fontWeight: 600 }}>
        {n} {label}
      </span>
    ) : null;
  return (
    <div
      style={{
        border: '1px solid var(--color-border, #e3e6ee)',
        borderRadius: 10,
        background: 'var(--color-surface, #fff)',
      }}
    >
      <button type="button" onClick={onToggle} style={{ ...certRowBtn }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform .15s',
              color: 'var(--color-text-tertiary,#697184)',
            }}
          >
            ▸
          </span>
          <b>{item.customer}</b>
          <span className="text-dim text-sm">{s.total} reports</span>
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {chip('with data', s.pass, '#00776b')}
          {chip('dead-loc', s.emptyDead, '#697184')}
          {chip('suspect', s.emptySuspect, '#8a5a06')}
          {chip('fail-render', s.failRender, '#b00f3a')}
          {chip('fail-parity', s.failParity, '#b00f3a')}
          <span
            style={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: clean ? '#00776b' : '#8a5a06',
              background: clean ? '#e5f4f2' : '#fff5e6',
              borderRadius: 999,
              padding: '2px 10px',
            }}
          >
            {clean ? 'No blockers' : 'Review'}
          </span>
        </span>
      </button>
      {open && <CertDetailTable customer={item.customer} />}
    </div>
  );
}

function CertDetailTable({ customer }: { customer: string }): JSX.Element {
  const detail = useCertification(customer);
  if (detail.isLoading)
    return (
      <div className="text-dim text-sm" style={{ padding: 12 }}>
        Loading…
      </div>
    );
  const reports = detail.data?.reports ?? [];
  return (
    <div style={{ overflowX: 'auto', borderTop: '1px solid var(--color-border,#e3e6ee)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--color-text-tertiary,#697184)' }}>
            <th style={certTh}>Report</th>
            <th style={{ ...certTh, textAlign: 'right' }}>Rows</th>
            <th style={certTh} title="Output renders in the delivered format">
              Render
            </th>
            <th style={certTh} title="Schedule (cron + time + tz) matches Jasper">
              Sched
            </th>
            <th style={certTh} title="Delivery method matches Jasper">
              Deliv
            </th>
            <th style={certTh} title="Export format matches Jasper">
              Fmt
            </th>
            <th style={certTh} title="Recipient set matches Jasper">
              Recip
            </th>
            <th style={certTh} title="Every parameter (incl. hidden) matches Jasper">
              Params
            </th>
            <th style={certTh} title="Schedule staged disabled">
              Off
            </th>
            <th style={certTh}>Result</th>
          </tr>
        </thead>
        <tbody>
          {reports.map((r: CertReport) => (
            <CertReportRow key={r.id} r={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CertReportRow({ r }: { r: CertReport }): JSX.Element {
  const [open, setOpen] = useState(false);
  const rc = r.recon;
  const pOk = rc ? rc.params.mismatched.length === 0 : r.checks.B_parity;
  return (
    <>
      <tr
        style={{ borderTop: '1px solid var(--color-border-subtle,#eef0f6)', cursor: 'pointer' }}
        onClick={() => setOpen((o) => !o)}
      >
        <td style={{ ...certTd, maxWidth: 320 }}>
          <span style={{ color: 'var(--color-text-tertiary,#697184)', marginRight: 6 }}>
            {open ? '▾' : '▸'}
          </span>
          {r.name}
        </td>
        <td style={{ ...certTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {r.rows ?? '–'}
        </td>
        <td style={certTd}>{tick(r.checks.C_render && r.checks.C_renderJson)}</td>
        <td style={certTd}>{dimTick(rc?.schedule.match)}</td>
        <td style={certTd}>{dimTick(rc?.delivery.match)}</td>
        <td style={certTd}>{dimTick(rc?.format.match)}</td>
        <td style={certTd}>{dimTick(rc?.recipients.match)}</td>
        <td style={certTd}>
          {rc ? tick(pOk) : dimTick(null)}
          {rc && rc.params.cosmetic.length > 0 ? (
            <span title="cosmetic title diff" style={{ color: '#8a5a06', marginLeft: 4 }}>
              *
            </span>
          ) : null}
        </td>
        <td style={certTd}>{tick(r.checks.G_disabled)}</td>
        <td style={certTd}>{certPill(r.status)}</td>
      </tr>
      {open && rc && (
        <tr>
          <td
            colSpan={10}
            style={{ padding: '4px 14px 16px 30px', background: 'var(--color-surface-2,#f7f8fb)' }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))',
                gap: 14,
                marginBottom: 12,
              }}
            >
              {dimBlock('Schedule', rc.schedule)}
              {dimBlock('Delivery method', rc.delivery)}
              {dimBlock('Export format', rc.format)}
              {dimBlock('Report period', rc.period)}
              {dimBlock('Client / scope', rc.scope)}
              {recipBlock(rc.recipients)}
            </div>
            <div
              style={{
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '.03em',
                color: 'var(--color-text-tertiary,#697184)',
                fontWeight: 700,
                margin: '4px 0 6px',
              }}
            >
              Parameters — {rc.params.matched}/{rc.params.total} match · {rc.params.hidden} hidden
              {rc.params.mismatched.length > 0 ? (
                <span style={{ color: '#b00f3a' }}> · {rc.params.mismatched.length} mismatch</span>
              ) : null}
            </div>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '0.75rem',
                background: 'var(--color-surface,#fff)',
                border: '1px solid var(--color-border,#e3e6ee)',
                borderRadius: 6,
              }}
            >
              <thead>
                <tr style={{ color: 'var(--color-text-tertiary,#697184)', textAlign: 'left' }}>
                  <th style={certTd}>Parameter</th>
                  <th style={certTd}>Jasper</th>
                  <th style={certTd}>Sage</th>
                  <th style={certTd}>Result</th>
                </tr>
              </thead>
              <tbody>
                {rc.params.detail.map((p) => (
                  <tr
                    key={p.name}
                    style={{ borderTop: '1px solid var(--color-border-subtle,#eef0f6)' }}
                  >
                    <td style={certTd}>
                      {p.name}
                      {p.hidden ? (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: '0.65rem',
                            color: '#697184',
                            border: '1px solid #d7dbe6',
                            borderRadius: 4,
                            padding: '0 4px',
                          }}
                        >
                          hidden
                        </span>
                      ) : null}
                    </td>
                    <td style={{ ...certTd, fontFamily: 'ui-monospace,Menlo,monospace' }}>
                      {p.jasper}
                    </td>
                    <td style={{ ...certTd, fontFamily: 'ui-monospace,Menlo,monospace' }}>
                      {p.sage}
                    </td>
                    <td style={certTd}>{paramStatusPill(p.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function dimBlock(
  label: string,
  d: { jasper: unknown; sage: unknown; match: boolean | null },
): JSX.Element {
  return (
    <div
      style={{
        border: '1px solid var(--color-border,#e3e6ee)',
        borderRadius: 6,
        padding: '8px 10px',
        background: 'var(--color-surface,#fff)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.72rem',
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        <span>{label}</span>
        {dimTick(d.match)}
      </div>
      <div style={{ fontSize: '0.74rem', color: '#697184' }}>
        Jasper: <span style={{ color: '#263238' }}>{String(d.jasper ?? '–')}</span>
      </div>
      <div style={{ fontSize: '0.74rem', color: '#697184' }}>
        Sage: <span style={{ color: '#263238' }}>{String(d.sage ?? '–')}</span>
      </div>
    </div>
  );
}

function recipBlock(r: {
  jasper: string[];
  sage: string[];
  missing: string[];
  extra: string[];
  match: boolean | null;
}): JSX.Element {
  return (
    <div
      style={{
        border: '1px solid var(--color-border,#e3e6ee)',
        borderRadius: 6,
        padding: '8px 10px',
        background: 'var(--color-surface,#fff)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.72rem',
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        <span>Recipients ({r.sage.length})</span>
        {dimTick(r.match)}
      </div>
      <div style={{ fontSize: '0.72rem', color: '#263238', wordBreak: 'break-all' }}>
        {r.sage.join(', ') || '–'}
      </div>
      {r.missing.length > 0 ? (
        <div style={{ fontSize: '0.72rem', color: '#b00f3a' }}>missing: {r.missing.join(', ')}</div>
      ) : null}
      {r.extra.length > 0 ? (
        <div style={{ fontSize: '0.72rem', color: '#8a5a06' }}>extra: {r.extra.join(', ')}</div>
      ) : null}
    </div>
  );
}

const PARAM_STATUS: Record<string, { label: string; fg: string }> = {
  match: { label: '✓ match', fg: '#00776b' },
  bridge: { label: '✓ bridge', fg: '#00776b' },
  note: { label: '✓ via template', fg: '#00776b' },
  unset: { label: 'not set (both)', fg: '#9aa2b1' },
  recomputed: { label: 'recomputed', fg: '#697184' },
  sonly: { label: 'set in Sage', fg: '#697184' },
  mismatch: { label: '✕ differs', fg: '#b00f3a' },
  dropped: { label: '✕ dropped', fg: '#b00f3a' },
  title: { label: 'title differs', fg: '#8a5a06' },
  titledrop: { label: 'title dropped', fg: '#8a5a06' },
};
function paramStatusPill(status: string): JSX.Element {
  const m = PARAM_STATUS[status] ?? { label: status, fg: '#697184' };
  return <span style={{ color: m.fg, fontWeight: 600 }}>{m.label}</span>;
}

function tick(ok: boolean): JSX.Element {
  return (
    <span style={{ color: ok ? '#00776b' : '#b00f3a', fontWeight: 700 }}>{ok ? '✓' : '✕'}</span>
  );
}
/** Dimension tick: true ✓, false ✕, null/undefined → em-dash (n/a, e.g. no subscription). */
function dimTick(m: boolean | null | undefined): JSX.Element {
  if (m == null) return <span style={{ color: '#9aa2b1' }}>–</span>;
  return tick(m);
}

const certRowBtn: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: '11px 14px',
  background: 'transparent',
  border: 0,
  cursor: 'pointer',
  font: 'inherit',
  textAlign: 'left',
  gap: 12,
};
const certTh: CSSProperties = {
  padding: '8px 12px',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '.03em',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
const certTd: CSSProperties = { padding: '8px 12px', verticalAlign: 'top' };
